// External Dependencies
import React from 'react';
import { useTranslation } from "react-i18next";
import { IconButton, ToggleButton, ToggleButtonGroup, Tooltip, Typography } from '@mui/material';
import CalendarMonthIcon from '@mui/icons-material/CalendarMonth';
// Internal Dependencies
import useAccount from '../../lib/useAccount';
import { USAGE_DESCRIPTIONS, USAGE_FILTER_TYPES } from './MyUsage.constants';
import { UseMyUsageActionsProps } from './MyUsage.interfaces';
// @ts-ignore
import { UcButtonChartDownloadV2 } from './UcButtonChartDownloadV2';
// @ts-ignore
import { UcHint } from '../common/UcHint';
// @ts-ignore
import { dateFormatter } from '../../lib/Formatters';
// @ts-ignore
import { useResponsive } from 'lib/useResponsive';
import { UcMeterSelectModular } from '../common/UcMeterSelectModular';
import { UcButtonGenerationChartDownloadV2 } from './UcButtonGenerationChartDownloadV2';

function useMyUsageActions(props: UseMyUsageActionsProps) {
    const { startDate, endDate, uom, csvDataToDownload, csvGenerationDataToDownload, setIsDownloadReady,
        orgTempUnit, hasCostModule, parentHandleOnChangeChartType, parentHandleOnChangeSelectedMeter, parentHandleOpenFilterDialog } = props;
    const { t } = useTranslation();
    const { account } = useAccount();
    const { isMobile, isTablet } = useResponsive();

    const [chartDescription, setChartDescription] = React.useState(USAGE_DESCRIPTIONS.DAY);

    const [selectedMeterOption, setSelectedMeterOption] = React.useState<any>(null);
    const [meterOptions, setMeterOptions] = React.useState<any>(null);

    const [chartType, setChartType] = React.useState(USAGE_FILTER_TYPES.DAY);

    const handleXMLClick = () => {
        setIsDownloadReady(true);
    }

    const getFileName = (fileType: string) => {
        const formattedCurrentDate = dateFormatter(new Date(), true);

        switch (chartType) {
            case USAGE_FILTER_TYPES.YEAR:
                return `myYearlyUsage_${account?.extAccountNumber}_${formattedCurrentDate}.${fileType}`;
            case USAGE_FILTER_TYPES.MONTH:
                return `myMonthlyUsage_${account?.extAccountNumber}_${formattedCurrentDate}.${fileType}`;
            case USAGE_FILTER_TYPES.DAY:
                return `myDailyUsage_${account?.extAccountNumber}_${formattedCurrentDate}.${fileType}`;
            default:
                return `myUsage_${account?.extAccountNumber}_${formattedCurrentDate}.${fileType}`;
        }
    }

    const handleOnChangeSelectedMeter = (option: any) => {
        setSelectedMeterOption(option);
        parentHandleOnChangeSelectedMeter(option);
    };

    const handleOnChangeChartType = (event: React.MouseEvent) => {
        const value = (event.target as HTMLButtonElement).value;
        switch (value) {
            case USAGE_FILTER_TYPES.YEAR:
                setChartDescription(USAGE_DESCRIPTIONS.YEAR);
                break;
            case USAGE_FILTER_TYPES.MONTH:
                setChartDescription(USAGE_DESCRIPTIONS.MONTH);
                break;
            case USAGE_FILTER_TYPES.DAY:
                setChartDescription(USAGE_DESCRIPTIONS.DAY);
                break;
        }
        setChartType(value);
        parentHandleOnChangeChartType(value);
    };

    let actionItems = [
        {
            component: (
                <UcHint
                    hintText={t(chartDescription)}
                    alertText={
                        isMobile
                            ? t("Tap a bar to view more info about it. \nSwipe left or right on the graph to reveal more dates if they're available.")
                            : t("Click on a bar to view more info about it.")
                    }
                />
            )
        },
        {
            hasMarginRight: true,
            component: (  
                <IconButton
                    title={t('Date Filter')}
                    onClick={parentHandleOpenFilterDialog}
                    aria-label="filter"
                    sx={{ color: 'primary.main' }}
                    data-cy-id={"ucDateFilter-iconButton"}
                >
                    <Tooltip title={t('Date Filter')} placement="top">
                        <CalendarMonthIcon />
                    </Tooltip>
                </IconButton>
            )
        },
        {
            hasMarginRight: true,
            component: (
                <UcMeterSelectModular
                    datacyid={"myUsageActions-ucMeterSelectModular"} 
                    startDate={startDate}
                    endDate={endDate}
                    uom={uom}
                    forcedWidth={(!isMobile && !isTablet) ? '50%' : '100%'}
                    onChange={handleOnChangeSelectedMeter}
                    onlyCommodity={false}
                    setMeterOptions={setMeterOptions}
                    marginTop={"1px"}
                />
            )
        },
        {
            hasMarginRight: true,
            component: (
                <ToggleButtonGroup
                    data-cy-id={"myUsageActions-toggleButtonGroup"}
                    value={chartType}
                    size="small"
                    exclusive
                    onChange={handleOnChangeChartType}
                    aria-label="ChartType"
                >
                    <ToggleButton color='primary' value="day" title={t('Day')} data-cy-id={"ucDateToggle-day"}>{t('Day')}</ToggleButton>
                    <ToggleButton color='primary' value="month" title={t('Month')} data-cy-id={"ucDateToggle-month"}>{t('Month')}</ToggleButton>
                    <ToggleButton color='primary' value="year" title={t('Year')} data-cy-id={"ucDateToggle-year"}>{t('Year')}</ToggleButton>
                </ToggleButtonGroup>
            )
        }
    ];

    if (csvDataToDownload.length > 0) {
        const chartDownloadComponent = {
            component: (
                <UcButtonChartDownloadV2
                    //@ts-ignore
                    data={csvDataToDownload}
                    selectedMeter={selectedMeterOption?.serialNo}
                    uom={uom}
                    csvTempUom={orgTempUnit}
                    filename={getFileName("csv")}
                    onXMLClick={handleXMLClick}
                    isXmlGenerating={false}
                    palletColor='primary.main'
                    hasCostModule={hasCostModule}
                />
            )
        };

        actionItems.splice(1, 0, chartDownloadComponent);
    }

    const generationActionItems = csvGenerationDataToDownload?.length > 0 ? 
    [
        {
            component: (
                <UcButtonGenerationChartDownloadV2
                    data={csvGenerationDataToDownload}
                    selectedMeter={selectedMeterOption?.serialNo}
                    uom={uom}
                    filename={"generation_" + getFileName("csv")}
                    palletColor='primary.main'
                />
            )
        }
    ] : [];

    if (isMobile) {
        generationActionItems.push({         
            component: (
                <UcHint
                    alertText={
                        <>
                            <Typography>
                                {t("Tap on a data point to select it. ")}
                            </Typography>
                            <Typography>
                                {t("Then tap inside the circle to view more information within a tooltip.")}
                            </Typography>
                        </>                        
                    }
                />
            )          
        })
    }

    return { actionItems, generationActionItems, getFileName, meterOptions };
}

export default useMyUsageActions;